import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Search, User as UserIcon } from "lucide-react";
import { formatDate, daysUntil } from "../lib/format";
import { divisionById, TASK_PRIORITY_LABEL } from "../lib/constants";
import { useAuth } from "../context/AuthContext";

const COLS = [
  { key: "Available", label: "Tersedia", tone: "bg-amber-50 border-amber-200 text-amber-800" },
  { key: "Claimed",   label: "Diambil",  tone: "bg-sky-50 border-sky-200 text-sky-800" },
  { key: "In Progress", label: "Dikerjakan", tone: "bg-emerald-50 border-emerald-200 text-emerald-800" },
  { key: "Done",      label: "Selesai",  tone: "bg-[#F0EBE0] border-[#E5E0D8] text-[#5C6066]" },
];

export default function TasksBoard() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [fDiv, setFDiv] = useState(() => {
    try { return localStorage.getItem("dwg_kanban_div") || "all"; } catch (e) { return "all"; }
  });
  const [busy, setBusy] = useState(null);
  const [dragId, setDragId] = useState(null);

  const load = () => api.get("/tasks").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    try { localStorage.setItem("dwg_kanban_div", fDiv); } catch (e) {}
  }, [fDiv]);

  const cols = useMemo(() => {
    const buckets = { Available: [], Claimed: [], "In Progress": [], Done: [] };
    for (const t of rows) {
      if (q) {
        const s = q.toLowerCase();
        if (!`${t.title} ${t.client_name || ""} ${t.booking_code || ""}`.toLowerCase().includes(s)) continue;
      }
      if (fDiv !== "all" && t.division_id !== fDiv) continue;
      const key = t.status === "To Do" ? "Available" : (buckets[t.status] !== undefined ? t.status : null);
      if (!key) continue;
      buckets[key].push(t);
    }
    return buckets;
  }, [rows, q, fDiv]);

  const claim = async (t) => {
    setBusy(t.id);
    try { await api.post(`/tasks/${t.id}/claim`, { note: "" }); toast.success("Tugas diambil"); load(); }
    catch (e) { if (!e?.__preview_blocked) toast.error(e?.response?.data?.detail || "Gagal ambil"); }
    finally { setBusy(null); }
  };
  const start = async (t) => {
    setBusy(t.id);
    try { await api.put(`/tasks/${t.id}`, { status: "In Progress" }); toast.success("Mulai dikerjakan"); load(); }
    catch (e) { if (!e?.__preview_blocked) toast.error("Gagal"); } finally { setBusy(null); }
  };
  const submitDone = async (t) => {
    setBusy(t.id);
    try { await api.post(`/tasks/${t.id}/submit-done`, { note: "" }); toast.success("Diklaim selesai — menunggu verifikasi"); load(); }
    catch (e) { if (!e?.__preview_blocked) toast.error("Gagal"); } finally { setBusy(null); }
  };

  const onDrop = async (colKey) => {
    const t = rows.find((x) => x.id === dragId);
    setDragId(null);
    if (!t || t.status === colKey) return;
    // Map column → action
    if (colKey === "Claimed" && t.status === "Available") return claim(t);
    if (colKey === "In Progress" && (t.status === "Claimed" || t.status === "To Do" || t.status === "Available")) {
      if (t.status === "Available") { await claim(t); }
      return start(t);
    }
    if (colKey === "Done") return submitDone(t);
    // Fallback simple PUT
    setBusy(t.id);
    try { await api.put(`/tasks/${t.id}`, { status: colKey }); load(); }
    catch (e) { if (!e?.__preview_blocked) toast.error("Gagal"); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-6" data-testid="tasks-board-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-[#1E2022]">Kanban Board Tugas</h1>
        <p className="text-sm text-[#5C6066] mt-1">Staff dapat melihat dan mengambil tugas sesuai divisi dalam tampilan kanban.</p>
      </div>
      <Card className="border border-[#E5E0D8] bg-white shadow-none p-3 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-[#8C9096]" />
          <Input className="pl-9 h-9" placeholder="Cari tugas atau booking..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="board-search" />
        </div>
        <Select value={fDiv} onValueChange={setFDiv}>
          <SelectTrigger className="w-44 h-9" data-testid="board-filter-div"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Divisi</SelectItem>
            {["div-mua","div-decor","div-photo","div-attire","div-music","div-wo","div-warawiri","div-effect","div-adat","div-snap","div-handcraft","div-studio"].map((d) => (
              <SelectItem key={d} value={d}>{divisionById(d)?.short || d}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {COLS.map((col) => (
          <div key={col.key}
               className={`rounded-lg border ${col.tone} p-2 min-h-[400px]`}
               data-testid={`board-col-${col.key}`}
               onDragOver={(e) => e.preventDefault()}
               onDrop={() => onDrop(col.key)}>
            <div className="px-2 py-1.5 flex items-center justify-between">
              <div className="font-display font-semibold text-sm">{col.label}</div>
              <span className="text-[11px] font-semibold opacity-70">{cols[col.key].length}</span>
            </div>
            <div className="space-y-2 mt-1">
              {cols[col.key].length === 0 && <div className="text-[11px] text-center py-6 opacity-60">Kosong</div>}
              {cols[col.key].map((t) => {
                const d = daysUntil(t.due_date);
                return (
                  <Card key={t.id}
                        draggable
                        onDragStart={() => setDragId(t.id)}
                        className="p-3 bg-white border border-[#E5E0D8] shadow-none cursor-grab active:cursor-grabbing"
                        data-testid={`board-card-${t.id}`}>
                    <div className="text-[10px] font-mono text-[#8C9096]">{t.booking_code || "PERSONAL"}</div>
                    <div className="font-medium text-sm mt-0.5 line-clamp-2">{t.title}</div>
                    {t.client_name && <div className="text-[11px] text-[#5C6066] mt-0.5">{t.client_name}</div>}
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      {t.division_id && <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold border ${divisionById(t.division_id)?.color || "bg-zinc-100 text-zinc-700 border-zinc-200"}`}>{divisionById(t.division_id)?.short || t.division_id}</span>}
                      <span className="text-[10px] text-[#8C9096]">{formatDate(t.due_date)} · <span className={d < 0 ? "text-rose-700 font-semibold" : ""}>{d < 0 ? `T-${Math.abs(d)}` : d === 0 ? "Hari ini" : `H-${d}`}</span></span>
                    </div>
                    {t.claimed_by_name && (
                      <div className="mt-2 text-[11px] text-[#5C6066] flex items-center gap-1">
                        <UserIcon className="w-3 h-3"/> {t.claimed_by_name}
                      </div>
                    )}
                    {col.key === "Available" && (
                      <Button size="sm" className="mt-2 h-7 w-full bg-[#B7A175] hover:bg-[#A08A5D] text-white text-xs" onClick={() => claim(t)} disabled={busy===t.id} data-testid={`board-claim-${t.id}`}>Ambil Tugas</Button>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
